import React, { useCallback, useState } from 'react';
import { Box, Button, Link, Typography, Tooltip, IconButton, Divider } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { useDropzone } from 'react-dropzone';
import { addFileToProject, DmProject, generateUploadUrl, deleteFileFromProject, updateDmProjectFile } from '../../api-services/dm-projects.client';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import GenerationModal from '../../components/GenerationModal';
import CommentsInput from '../../components/CommentsInput';
import { useUpdateProject } from '../../hooks/useUpdateProject';
import { useToast } from '../../context/toast-context';

const SUPPORTED_FILE_TYPES = {
  'text/csv': '.csv',
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
};

const SUPPORTED_FILE_EXTENSIONS = ['.csv', '.pdf', '.xlsx', '.xls'];

interface DocumentsTabProps {
  project: DmProject;
  fetchProject: () => void;
  setTab: (tab: number) => void;
  tab: number;
  setResponseLLM: (response: any) => void;
  responseLLM: any;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ project, fetchProject, setTab, setResponseLLM, responseLLM, tab }) => {
  const { showToast } = useToast();
  const { projectId } = useParams<{ projectId: string }>();
  const [isUploading, setIsUploading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const { updateProjectHook } = useUpdateProject();

  const files = project?.files ? project.files.map(file => ({
    ...file,
    id: file.s3Key,
  })) : [];

  const [generationModalOpen, setGenerationModalOpen] = useState(false);
  const [projectNotes, setProjectNotes] = useState(project?.notes || '');

  const validateFile = (file: File): boolean => {
    if (SUPPORTED_FILE_TYPES[file.type as keyof typeof SUPPORTED_FILE_TYPES]) {
      return true;
    }

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return SUPPORTED_FILE_EXTENSIONS.includes(fileExtension);
  };

  const handleSendToDealRoom = async (s3Key: string, currentStatus: boolean) => {
    try {
      setTableLoading(true);
      await updateDmProjectFile(projectId, s3Key, { sendToDealRoom: !currentStatus });
      showToast({
        message: `File ${!currentStatus ? 'sent to' : 'removed from'} deal room successfully`,
        severity: 'success',
      });
      await fetchProject();
    } catch (error) {
      showToast({
        message: 'Error updating file status',
        severity: 'error',
      });
    } finally {
      setTableLoading(false);
    }
  };

  const handleDeleteFile = async (s3Key: string) => {
    try {
      setTableLoading(true);
      await deleteFileFromProject(projectId, s3Key);

      // remove timestamp of last upload in localStorage
      localStorage.setItem(`lastUploadTimestamp`, Date.now().toString());

      showToast({
        message: 'File deleted successfully',
        severity: 'success',
      });
      await fetchProject();
    } catch (error) {
      showToast({
        message: 'Error deleting file',
        severity: 'error',
      });
    } finally {
      setTableLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerationModalOpen(true);
  };

  const handleProjectNotesChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    await updateProjectHook(projectId, { notes: e.target.value })
    fetchProject();
  };


  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'NAME',
      flex: 3,
    },
    {
      field: 'fileType',
      headerName: 'FILE TYPE',
      flex: 1,
      valueGetter: (params) => {
        const fileName = params.row.name || '';
        const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
        return extension || params.row.fileType;
      },
    },
    {
      field: 'comments',
      headerName: 'COMMENTS',
      flex: 2,
      renderCell: (params) => (
        <CommentsInput
          projectId={projectId}
          fileId={params.row.s3Key}
          defaultValue={params.row.comments}
          onFinish={fetchProject}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IconButton
            onClick={() => handleSendToDealRoom(params.row.s3Key, params.row.sendToDealRoom)}
          >
            <Tooltip title={params.row.sendToDealRoom ? 'Remove from Deal Room' : 'Send to Deal Room'}>
              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d={params.row.sendToDealRoom
                  ? "M8.66634 7.08333V3.75L2.83301 9.58333L8.66634 15.4167V12C12.833 12 15.7497 13.3333 17.833 16.25C17.0497 12.0833 14.4997 7.91667 8.66634 7.08333Z"
                  : "M11.9997 7.08333V3.75L17.833 9.58333L11.9997 15.4167V12C7.83301 12 4.91634 13.3333 2.83301 16.25C3.66634 12.0833 6.16634 7.91667 11.9997 7.08333Z"
                }
                  fill={params.row.sendToDealRoom ? '#D32F2F' : '#9C27B0'} />
              </svg>
            </Tooltip>
          </IconButton>
          <IconButton onClick={() => handleDeleteFile(params.row.s3Key)} >
            <Tooltip title="Remove file">
              <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.33333 15.8333C5.33333 16.75 6.08333 17.5 7 17.5H13.6667C14.5833 17.5 15.3333 16.75 15.3333 15.8333V5.83333H5.33333V15.8333ZM16.1667 3.33333H13.25L12.4167 2.5H8.25L7.41667 3.33333H4.5V5H16.1667V3.33333Z" fill="#9B51DF" />
              </svg>
            </Tooltip>
          </IconButton>
        </Box>
      ),
    },
  ];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      setIsUploading(true);
      const file = acceptedFiles[0];

      if (!validateFile(file)) {
        showToast({
          message: 'File type not supported',
          severity: 'error',
        });
        return;
      }

      const { url, key, fileName } = await generateUploadUrl(file.name, file.type, projectId);

      const uploadRes = await axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`uploading... ${percentCompleted}%`);
        },
      });

      if (uploadRes?.status === 200) {
        showToast({
          message: 'File uploaded successfully',
          severity: 'success',
        })

        // save timestamp of last upload in localStorage
        localStorage.setItem(`lastUploadTimestamp`, Date.now().toString());

        const result = await addFileToProject({
          projectId,
          fileName: fileName,
          fileType: file.type,
          s3Key: key,
        });
        console.log(result)
        await fetchProject();
      } else {
        showToast({
          message: 'Error uploading file',
          severity: 'error',
        })
      }
    } catch (error) {
      showToast({
        message: 'Error uploading file',
        severity: 'error',
      })
    } finally {
      setIsUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    disabled: isUploading,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    onDropRejected: (rejectedFiles) => {
      showToast({
        message: 'File type not supported. Please upload CSV, PDF, XLSX, or XLS files only.',
        severity: 'error',
      });
    },
  });

  /*
  useEffect(() => {
    fetchProject();
  }, []);
  */

  return (
    <Box>
      {/* Dropzone area */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? '#6435C9' : '#D3D3D3',
          background: isDragActive ? '#F3F0FC' : '#fff',
          borderRadius: 2,
          p: 3,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'relative',
          transition: 'border-color 0.2s, background 0.2s',
          opacity: isUploading ? 0.6 : 1,
          pointerEvents: isUploading ? 'none' : 'auto',
        }}
      >
        <input {...getInputProps()} />
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Link
            component="button"
            underline="hover"
            sx={{ color: '#6435C9', fontWeight: 500, mr: 1 }}
            onClick={open}
            disabled={isUploading}
          >
            Select file
          </Link>
          <Typography sx={{ color: '#888', fontWeight: 400, fontSize: 16 }}>
            {isUploading ? 'Uploading...' : 'or drag and drop here (CSV, PDF, XLSX, XLS)'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            sx={{
              background: '#000',
              color: '#fff',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: 16,
              px: 3,
              boxShadow: 'none',
              '&:hover': { background: '#222' },
              '&:disabled': { background: '#666' },
            }}
            onClick={open}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Browse Files'}
          </Button>
        </Box>
        {isDragActive && (
          <Typography sx={{ color: '#6435C9', fontWeight: 600, fontSize: 18, mt: 2, width: '100%', textAlign: 'center' }}>
            Drop the files here ...
          </Typography>
        )}
        <Typography sx={{ color: '#666', fontWeight: 400, fontSize: 14, mt: 1 }}>
          Supported formats: CSV, PDF, XLSX, XLS
        </Typography>
      </Box>


      {/* Uploaded Files Table */}
      <Typography sx={{ fontWeight: 700, fontSize: 28, mb: 2 }}>
        Uploaded Files
      </Typography>
      <Box sx={{
        background: '#fff',
        borderRadius: 2,
        boxShadow: 1,
        p: 0,
        mb: 3,
        opacity: isUploading ? 0.6 : 1,
        pointerEvents: isUploading ? 'none' : 'auto',
      }}>
        <DataGridPro
          rows={files}
          columns={columns}
          autoHeight
          hideFooter
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          loading={tableLoading || isUploading}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              background: '#fff',
              fontWeight: 700,
              fontSize: 16,
              borderBottom: '2px solid #eee',
            },
            '& .MuiDataGrid-cell': {
              fontSize: 16,
              borderBottom: '1px solid #eee',
            },
            '& .MuiDataGrid-row': {
              minHeight: 60,
              maxHeight: 60,
            },
            '& .MuiDataGrid-virtualScroller': {
              minHeight: 300,
            },
          }}
        />
      </Box>
      <Box sx={{
        mb: 3,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, minWidth: 140, mr: 2, mb: 0, }}>
          Project Notes
        </Typography>
        <textarea
          value={projectNotes}
          onChange={e => setProjectNotes(e.target.value)}
          placeholder="Add text"
          rows={3}
          onBlur={handleProjectNotesChange}
          style={{
            flex: 1,
            minHeight: '40px',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            resize: 'vertical',
            width: '100%',
          }}
        />
      </Box>

      <Divider sx={{ my: 3 }} />


      <Button
        fullWidth
        variant="contained"
        sx={{
          background: '#6435C9',
          color: '#fff',
          borderRadius: 2,
          fontWeight: 600,
          fontSize: 18,
          py: 1.5,
          boxShadow: 'none',
          mb: 1,
          '&:hover': { background: '#5127B0' },
          '&:disabled': { background: '#9B7EDF' },
        }}
        disabled={isUploading || tableLoading || !project.files?.length}
        onClick={handleGenerate}
      >
        {responseLLM ? 'Re-Generate' : 'Generate'}
      </Button>
      <GenerationModal
        open={generationModalOpen}
        onClose={() => setGenerationModalOpen(false)}
        projectId={projectId}
        setTab={setTab}
        setTempResponseLLM={setResponseLLM}
      />
    </Box>
  );
};

export default DocumentsTab; 